<template lang="pug">

  ValidationProvider(
    :rules="addRegexToValidationRules"
    v-slot="{ errors }"
    tag="div"
    :vid="$attrs.id"
  )
    div(
      :class="[{ 'error-styles': ( errors[0] || maxNumberCharactersExceed ) }, { 'hidden-field': hidden }, { 'disabled-field': $attrs.disabled },  'lf-textarea-container']"
    )
      span(:class="[{ 'required-field': requiredField }, 'label-text']") {{ $attrs.label }}
      div(class="lf-textarea-input")
        textarea(
          v-bind="$attrs"
          :class="[{ 'error-styles': errors[0] || maxNumberCharactersExceed }, 'lf-textarea']"
          :value="innerValue"
          @input="emitValue"
          :maxlength="$attrs.maxlength + 1"
        )
        span(
          v-if="value && value.length"
          :class="[icons.clearIcon, 'clean-icon']"
          @click.stop="cleanText"
        )
      div(class="messages-container")
        div(class="messages")
          span(v-if="errors.length" class="alert-message") {{ errors[0] }}
          span(v-else class="help-message") {{ helpText }}
        div(
          v-if="$attrs.maxlength"
          :class="[{ 'limit-reached': maxNumberCharactersExceed }, 'counter']"
        ) {{ value ? value.length : '0' }} / {{ $attrs.maxlength }}

</template>

<script lang="ts">
import { Vue, Component, Prop, Emit, Watch } from 'vue-property-decorator'
import { ValidationProvider } from 'vee-validate'
import { Icons } from '@/icons/icons'
import { regexPattern } from '@/helpers/helpers'

@Component({
  components: {
    ValidationProvider
  }
})
export default class LexonTextAreaComponent extends Vue {
  @Prop({
    type: String
  })
  validationRules!: string

  @Prop({
    type: String,
    default: ''
  })
  value!: string

  @Prop({
    type: String
  })
  helpText!: string

  @Prop({
    type: Boolean,
    default: false
  })
  hidden!: boolean

  icons = {
    clearIcon: Icons.CLOSE
  }

  innerValue = ''

  @Emit()
  emitValue(event: Event) {
    const target = event.target as HTMLInputElement
    this.$emit('input', target.value)
  }

  @Emit()
  cleanText() {
    return this.$emit('input', '')
  }

  @Watch('innerValue')
  changedValue(newVal: string) {
    this.$emit('input', newVal)
  }

  @Watch('value')
  changeInnerValue(newVal: string) {
    this.innerValue = newVal
  }

  get maxNumberCharactersExceed() {
    return this.value && this.value.length > Number(this.$attrs.maxlength)
  }

  get requiredField() {
    return this.validationRules && this.validationRules.includes('required') ? true : false
  }

  get addRegexToValidationRules() {
    if (this.validationRules && this.validationRules.length) {
      return `${this.validationRules}|regex:${regexPattern}`
    } else {
      return `regex:${regexPattern}`
    }
  }

  created() {
    if (this.value) {
      this.innerValue = this.value
    }
  }
}
</script>

<style lang="scss" scoped>
.lf-textarea-container {
  @include flex($flex-direction: column, $align-items: flex-start);
  @include textfield-messages;
  position: relative;
  width: 100%;

  &.disabled-field {
    .label-text,
    .messages-container .help-message,
    .messages-container .counter {
      color: $disabled-color;
      pointer-events: none;
    }

    &::after {
      color: $disabled-color;
    }

    .lf-textarea-input textarea:hover {
      border-color: $disabled-border-color;
      background-color: $disabled-background;
    }

    .lf-textarea-input {
      background-color: $disabled-background;

      .lf-textarea {
        border-color: $disabled-border-color;
        color: $disabled-color;
      }

      &:hover .clean-icon {
        display: none;
      }
    }

    .required-field.label-text::after {
      color: $tertiary-persianRed-050;
    }
  }

  &.hidden-field {
    display: none;
  }

  &.error-styles {
    .lf-textarea {
      border-color: $error-color !important;
      color: $neutral-grey-800 !important;

      &::placeholder {
        color: $neutral-grey-600 !important;
      }
    }

    .clean-icon {
      color: $error-color;
    }
  }

  .lf-textarea-input {
    width: 100%;
    height: 120px;

    &:hover .lf-textarea {
      background-color: $main-050;

      &:focus {
        background-color: $neutral-white-000;
      }
    }

    &:hover .clean-icon {
      display: block;
    }

    .lf-textarea {
      @include textfield-styles;
      @include scroll-styles;
      resize: none;
      height: 120px;
      padding: $spacing-xs 36px $spacing-xs $spacing-xs;

      &:focus ~ .clean-icon {
        display: block;
      }
    }
  }

  .label-text {
    @include label-text;
  }

  .clean-icon {
    position: absolute;
    top: 34px;
    right: 12px;
    cursor: pointer;
    font-size: 12px;
    color: $main-1000;
    display: none;
  }

  .messages-container {
    @include flex($justify-content: space-between);
    width: 100%;

    .counter {
      @include milano-regular-14;
      color: $neutral-grey-600;

      &.limit-reached {
        color: $error-color;
      }
    }
  }
}

::-webkit-resizer {
  display: none;
}

.lf-textarea-container.error-styles::after {
  color: $error-color;
}
</style>
